import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { statisticsApi } from '../../services/statisticsApi';
import type { PlayerGlobalStats } from '../../services/statisticsApi';
import '../Dashboard/Dashboard.css';
import './ProfilePage.css';

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<PlayerGlobalStats | null>(null);
  const [achievements, setAchievements] = useState<any[]>([]);
  const [levelInfo, setLevelInfo] = useState({ level: 1, xp: 0, nextLevelXp: 100, progress: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, [user]);

  const loadStats = async () => {
    const userId = user?.id || user?._id;
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const globalStats = await statisticsApi.getPlayerGlobalStats(userId);
      setStats(globalStats);
      setLevelInfo(statisticsApi.calculatePlayerLevel(globalStats));
      setAchievements(statisticsApi.calculateAchievements(globalStats));
    } catch (error) {
      console.error('Error loading profile stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-view">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="profile-view">
      <div className="view-header modern-minimal">
        <div className="header-content">
          <div className="header-icon">👤</div>
          <div className="header-text">
            <h1>Profile & Statistics</h1>
            <p>View your game statistics and manage your account settings.</p>
          </div>
        </div>
      </div>

      <div className="profile-content">
        {/* User Info Section */}
        <div className="profile-section">
          <h2>User Information</h2>
          <div className="profile-card">
            <div className="profile-info">
              <div className="info-item">
                <span className="label">Username:</span>
                <span className="value">{user?.username || 'Not available'}</span>
              </div>
              <div className="info-item">
                <span className="label">Email:</span>
                <span className="value">{user?.email || 'Not available'}</span>
              </div>
              <div className="info-item">
                <span className="label">Member since:</span>
                <span className="value">
                  {user?.createdAt 
                    ? new Date(user.createdAt).toLocaleDateString() 
                    : 'Not available'
                  }
                </span>
              </div>
              <div className="info-item">
                <span className="label">Level:</span>
                <span className="value">Level {levelInfo.level} ({levelInfo.xp}/{levelInfo.nextLevelXp} XP)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Game Statistics Section */}
        <div className="profile-section">
          <h2>Game Statistics</h2>
          <div className="stats-grid-compact">
            <div className="stat-card">
              <div className="stat-icon">🎮</div>
              <div className="stat-info">
                <div className="stat-value">{stats?.totalGamesPlayed || 0}</div>
                <div className="stat-label">Games Played</div>
                <div className="stat-detail">{stats?.totalRoomsPlayed || 0} rooms</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🏆</div>
              <div className="stat-info">
                <div className="stat-value">{stats?.totalGamesWon || 0}</div>
                <div className="stat-label">Games Won</div>
                <div className="stat-detail">{((stats?.totalGamesPlayed || 0) - (stats?.totalGamesWon || 0))} losses</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-info">
                <div className="stat-value">{stats?.overallWinRate.toFixed(1) || 0}%</div>
                <div className="stat-label">Win Rate</div>
                <div className="stat-detail">{(stats?.overallWinRate || 0) >= 50 ? 'Above avg!' : 'Keep going!'}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💎</div>
              <div className="stat-info">
                <div className="stat-value">{stats?.totalScore.toLocaleString() || 0}</div>
                <div className="stat-label">Total Score</div>
                <div className="stat-detail">{stats?.averageScore.toFixed(0) || 0} avg</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⭐</div>
              <div className="stat-info">
                <div className="stat-value">{stats?.bestRoomScore || 0}</div>
                <div className="stat-label">Best Score</div>
                <div className="stat-detail">Personal record</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-info">
                <div className="stat-value">{Math.round((stats?.averageScore || 0) / 5) * 5}</div>
                <div className="stat-label">Avg Score</div>
                <div className="stat-detail">Per game</div>
              </div>
            </div>
          </div>
        </div>

        {/* Achievements Section */}
        <div className="profile-section">
          <h2>Achievements ({achievements.length})</h2>
          {achievements.length > 0 ? (
            <div className="achievements-grid-compact">
              {achievements.map((achievement) => (
                <div key={achievement.id} className="achievement-card-compact">
                  <span className="achievement-icon">{achievement.icon}</span>
                  <div className="achievement-info">
                    <div className="achievement-name">{achievement.name}</div>
                    <div className="achievement-description">{achievement.description}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-message">Play games to unlock achievements!</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
